import { useCallback, useEffect, useRef } from "react";
import { useCommands } from "@components/commands/useCommands";
import { CommandsDirectory } from "@components/commands/lib";

interface UseAccrualDraftBulkCommandActionsParams {
    selectedCount: number;
    onConfirm?: () => void;
    confirmLabel: string;
    onDiscard: () => void;
    onRerun?: () => void;
    onMove?: () => void;
    moveLabel: string;
    onConfirmCounterparties?: () => void;
    counterpartyLabel: "vendors" | "customers";
}

export function useAccrualDraftBulkCommandActions({
    selectedCount,
    onConfirm,
    confirmLabel,
    onDiscard,
    onRerun,
    onMove,
    moveLabel,
    onConfirmCounterparties,
    counterpartyLabel,
}: UseAccrualDraftBulkCommandActionsParams) {
    const {
        open: openCommands,
        registerOpenOverride,
        unregisterOpenOverride,
    } = useCommands();

    const handleOpenBulkActions = useCallback(() => {
        if (selectedCount === 0) {
            return;
        }
        openCommands(CommandsDirectory.ACCRUAL_DRAFT_BULK, {
            selectedAccrualDraftCount: selectedCount,
            onAccrualDraftBulkConfirm: onConfirm,
            accrualDraftBulkConfirmLabel: confirmLabel,
            onAccrualDraftBulkDiscard: onDiscard,
            onAccrualDraftBulkRerun: onRerun,
            onAccrualDraftBulkMove: onMove,
            accrualDraftBulkMoveLabel: moveLabel,
            onAccrualDraftBulkConfirmCounterparties: onConfirmCounterparties,
            accrualDraftCounterpartyLabel: counterpartyLabel,
        });
    }, [
        confirmLabel,
        counterpartyLabel,
        moveLabel,
        onConfirm,
        onConfirmCounterparties,
        onDiscard,
        onMove,
        onRerun,
        openCommands,
        selectedCount,
    ]);

    const handleOpenBulkActionsRef = useRef(handleOpenBulkActions);
    handleOpenBulkActionsRef.current = handleOpenBulkActions;

    useEffect(() => {
        if (selectedCount === 0) {
            return undefined;
        }
        registerOpenOverride(() => handleOpenBulkActionsRef.current());
        return () => unregisterOpenOverride();
    }, [registerOpenOverride, selectedCount, unregisterOpenOverride]);

    return { handleOpenBulkActions };
}
